import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Filter, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useBrowseProducts } from "@/hooks/useBrowseProducts";
import { useBrowseFilters } from "@/hooks/useBrowseFilters";
import { useCategoriesWithProductsByType, type Category } from "@/hooks/useCategories";
import { BrowseFilters } from "@/components/browse/BrowseFilters";
import { BrowseProductGrid } from "@/components/browse/BrowseProductGrid";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { useEditMode } from "@/contexts/EditModeContext";

const Browse = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  const { isEditMode } = useEditMode();
  const { data: products = [], isLoading, error, refetch } = useBrowseProducts(isEditMode);
  const { isAdmin } = useAuth();

  const typeFilter = searchParams.get('type');
  const isEducation = typeFilter === 'education';
  const categoryServiceType = isEducation ? 'education' : 'non-education';
  const { data: categories = [] } = useCategoriesWithProductsByType(categoryServiceType) as { data: Category[] };

  const integrationOptions = [
    "Zapier", "Make (Integromat)", "Google Calendar", "Google Contacts",
    "Gmail", "Slack", "Follow Up Boss", "KVCore", "RealScout",
    "Mailchimp", "Stripe", "Twilio", "API / Webhooks",
  ];

  const allProducts = products || [];
  const sortedProducts = isAdmin 
    ? [...allProducts].sort((a, b) => a.is_published === b.is_published ? 0 : a.is_published ? -1 : 1)
    : allProducts;

  const filters = useBrowseFilters({
    products: sortedProducts,
    isEditMode,
    typeFilter,
    searchParams
  });

  const headerConfig = isEducation 
    ? { title: "Browse Courses & More", subtitle: "Learn from industry leaders and level up your skills" }
    : { title: "Browse Agent Tools & Services", subtitle: "Everything you need to grow your real estate business" };

  const displayProducts = filters.sortedFilteredProducts.slice(0, visibleCount);

  // Intersection Observer for pagination
  useEffect(() => {
    if (!loadMoreRef.current || displayProducts.length >= filters.sortedFilteredProducts.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => Math.min(prev + 20, filters.sortedFilteredProducts.length));
        }
      },
      { threshold: 0.1 }
    );
    
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [displayProducts.length, filters.sortedFilteredProducts.length]);

  const handleClearSearch = () => {
    filters.setSearchTerm('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        <div className="px-4 lg:px-6 xl:px-8 py-6">
          <div className="flex gap-8">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:block w-60 shrink-0 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto scrollbar-thin-hover">
              <BrowseFilters
                variant="desktop"
                categories={categories}
                isEducation={isEducation}
                priceRange={filters.priceRange}
                selectedCategories={filters.selectedCategories}
                circleSelectOnly={filters.circleSelectOnly}
                selectedBestFor={filters.selectedBestFor}
                selectedIntegrations={filters.selectedIntegrations}
                selectedSkillLevel={filters.selectedSkillLevel}
                selectedDurations={filters.selectedDurations}
                selectedInclusions={filters.selectedInclusions}
                showAllIntegrations={filters.showAllIntegrations}
                integrationOptions={integrationOptions}
                onPriceRangeChange={filters.setPriceRange}
                onCategoryToggle={filters.handleCategoryToggle}
                onCircleSelectChange={filters.setCircleSelectOnly}
                onBestForToggle={filters.handleBestForToggle}
                onIntegrationToggle={filters.handleIntegrationToggle}
                onSkillLevelChange={filters.setSelectedSkillLevel}
                onDurationToggle={filters.handleDurationToggle}
                onInclusionToggle={filters.handleInclusionToggle}
                onShowAllIntegrationsToggle={() => filters.setShowAllIntegrations(!filters.showAllIntegrations)}
              />
            </aside>

            <div className="flex-1">
              {/* Header Section */}
              <div className="mb-6">
                <div className="md:flex md:items-start md:justify-between md:gap-4 mb-4">
                  <div className="md:flex-1 space-y-2">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-0">
                      {filters.searchTerm ? `Searching "${filters.searchTerm}"` : headerConfig.title}
                    </h1>
                    <p className="hidden md:block text-muted-foreground text-sm md:text-base mb-4 md:mb-0">
                      {headerConfig.subtitle}
                    </p>
                    <p className="hidden md:block text-sm text-muted-foreground">
                      {isLoading ? 'Loading...' : `${displayProducts.length} ${isEducation ? 'courses' : 'products'}`}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between md:justify-end gap-3 md:pt-1">
                    {/* Mobile Filters */}
                    <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                      <SheetTrigger asChild>
                        <Button variant="outline" className="lg:hidden">
                          <Filter className="h-4 w-4 mr-2" />
                          Filters
                        </Button>
                      </SheetTrigger>
                      <SheetContent side="left" className="w-80 overflow-y-auto px-6">
                        <SheetHeader className="mb-4">
                          <SheetTitle>Filters</SheetTitle>
                        </SheetHeader>
                        <BrowseFilters
                          variant="mobile"
                          categories={categories}
                          isEducation={isEducation}
                          priceRange={filters.priceRange}
                          selectedCategories={filters.selectedCategories}
                          circleSelectOnly={filters.circleSelectOnly}
                          selectedBestFor={filters.selectedBestFor}
                          selectedIntegrations={filters.selectedIntegrations}
                          selectedSkillLevel={filters.selectedSkillLevel}
                          selectedDurations={filters.selectedDurations}
                          selectedInclusions={filters.selectedInclusions}
                          showAllIntegrations={filters.showAllIntegrations}
                          integrationOptions={integrationOptions}
                          onPriceRangeChange={filters.setPriceRange}
                          onCategoryToggle={filters.handleCategoryToggle}
                          onCircleSelectChange={filters.setCircleSelectOnly}
                          onBestForToggle={filters.handleBestForToggle}
                          onIntegrationToggle={filters.handleIntegrationToggle}
                          onSkillLevelChange={filters.setSelectedSkillLevel}
                          onDurationToggle={filters.handleDurationToggle}
                          onInclusionToggle={filters.handleInclusionToggle}
                          onShowAllIntegrationsToggle={() => filters.setShowAllIntegrations(!filters.showAllIntegrations)}
                        />
                      </SheetContent>
                    </Sheet>
                    
                    {/* Sort Dropdown */}
                    <Select value={filters.sortBy} onValueChange={filters.setSortBy}>
                      <SelectTrigger className="w-[140px]">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        {isEducation ? (
                          <>
                            <SelectItem value="recommended">Recommended</SelectItem>
                            <SelectItem value="most-popular">Most Popular</SelectItem>
                            <SelectItem value="newest">Newest</SelectItem>
                            <SelectItem value="rating">Highest Rated</SelectItem>
                            <SelectItem value="shortest">Shortest First</SelectItem>
                            <SelectItem value="longest">Longest First</SelectItem>
                            <SelectItem value="price-low">Price: Low to High</SelectItem>
                            <SelectItem value="price-high">Price: High to Low</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="recommended">Recommended</SelectItem>
                            <SelectItem value="newest">Newest</SelectItem>
                            <SelectItem value="ending">Ending soon</SelectItem>
                            <SelectItem value="price-low">Price: Low to High</SelectItem>
                            <SelectItem value="price-high">Price: High to Low</SelectItem>
                            <SelectItem value="rating">Rating</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              {/* Clear search button */}
              {filters.searchTerm && (
                <Button variant="ghost" size="sm" onClick={handleClearSearch}>
                  <X className="h-4 w-4 mr-1" />
                  Clear search
                </Button>
              )}

              {/* Product Grid */}
              <BrowseProductGrid
                products={filters.sortedFilteredProducts}
                isEducation={isEducation}
                isLoading={isLoading}
                error={error}
                isAdmin={isAdmin}
                visibleCount={visibleCount}
                totalCount={filters.sortedFilteredProducts.length}
                loadMoreRef={loadMoreRef}
                onRefetch={refetch}
                onClearFilters={filters.clearAllFilters}
              />
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Browse;
